// Phase 6B · Reusable customer email panel.
// Compose → preview → one-shot confirm → send.
// Mountable from CRM/Lead/Invoice/Roster surfaces by passing
// `sourceEntityType` and `sourceEntityId` props.
//
// Additive. No modification to any locked component.
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  listTemplates, proposeOutbound, confirmOutbound,
} from "@/lib/commOutbound";

const NO_TEMPLATE = "__none__";

export default function CustomerEmailPanel({
  sourceEntityType = "manual",
  sourceEntityId = null,
  defaultRecipients = [],
  defaultSubject = "",
  defaultBodyHtml = "",
  attachmentRefs = [],
  onSent,
  triggerLabel = "Email customer",
  triggerVariant = "default",
}) {
  const [open, setOpen] = useState(false);
  const [recipientsInput, setRecipientsInput] = useState(defaultRecipients.join(", "));
  const [subject, setSubject] = useState(defaultSubject);
  const [bodyHtml, setBodyHtml] = useState(defaultBodyHtml);
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [pending, setPending] = useState(null); // {pending_id, confirmation_token, payload_sha256, preview}
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open && templates.length === 0) {
      listTemplates().then(setTemplates).catch(() => {});
    }
  }, [open, templates.length]);

  const applyTemplate = (id) => {
    const normalized = id === NO_TEMPLATE ? "" : id;
    setTemplateId(normalized);
    if (!normalized) return;
    const t = templates.find((x) => x.id === normalized);
    if (t) { setSubject(t.subject); setBodyHtml(t.body_html); }
  };

  const handlePreview = async () => {
    const recipients = recipientsInput.split(",").map(s => s.trim()).filter(Boolean);
    if (recipients.length === 0) return toast.error("Add at least one recipient");
    if (!subject.trim()) return toast.error("Subject required");
    if (!bodyHtml.trim()) return toast.error("Body required");
    try {
      const resp = await proposeOutbound({
        recipients, subject, body_html: bodyHtml,
        template_id: templateId || null,
        source_entity_type: sourceEntityType,
        source_entity_id: sourceEntityId,
        attachment_refs: attachmentRefs,
      });
      setPending(resp);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.code || "Failed to prepare email");
    }
  };

  const handleConfirmSend = async () => {
    if (!pending) return;
    setSending(true);
    try {
      const res = await confirmOutbound(pending.pending_id, pending.confirmation_token);
      if (res?.ok) {
        toast.success("Email sent");
        onSent?.(res.log);
        setOpen(false);
        setPending(null);
        setRecipientsInput(defaultRecipients.join(", "));
        setSubject(defaultSubject);
        setBodyHtml(defaultBodyHtml);
      } else {
        toast.error(res?.log?.failure_reason || "Send failed");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail?.code || "Confirmation failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Button
        data-testid="customer-email-open-btn"
        variant={triggerVariant}
        onClick={() => setOpen(true)}
      >
        {triggerLabel}
      </Button>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setPending(null); }}>
        <DialogContent data-testid="customer-email-panel" className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Send customer email</DialogTitle>
            <DialogDescription>
              The AI drafts, you authorise. Nothing leaves until you confirm the exact payload below.
            </DialogDescription>
          </DialogHeader>

          {!pending ? (
            <div className="space-y-4">
              <div>
                <Label>Template</Label>
                <Select value={templateId || NO_TEMPLATE} onValueChange={applyTemplate}>
                  <SelectTrigger data-testid="customer-email-template-select">
                    <SelectValue placeholder="(no template)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_TEMPLATE}>(no template)</SelectItem>
                    {templates.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>To (comma separated)</Label>
                <Input
                  data-testid="customer-email-to-input"
                  value={recipientsInput}
                  onChange={(e) => setRecipientsInput(e.target.value)}
                  placeholder="client@example.com, cc@example.com"
                />
              </div>
              <div>
                <Label>Subject</Label>
                <Input
                  data-testid="customer-email-subject-input"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                />
              </div>
              <div>
                <Label>Body (HTML)</Label>
                <Textarea
                  data-testid="customer-email-body-input"
                  rows={10}
                  value={bodyHtml}
                  onChange={(e) => setBodyHtml(e.target.value)}
                />
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="text-xs uppercase tracking-wide text-neutral-500">
                Bound payload hash
              </div>
              <div
                data-testid="customer-email-payload-hash"
                className="font-mono text-xs break-all bg-neutral-50 border border-neutral-200 rounded-md p-2"
              >
                {pending.payload_sha256}
              </div>
              <div className="text-xs uppercase tracking-wide text-neutral-500">Preview</div>
              <pre
                data-testid="customer-email-preview"
                className="font-mono text-xs bg-neutral-50 border border-neutral-200 rounded-md p-2 overflow-x-auto whitespace-pre-wrap break-all"
              >
                {JSON.stringify(pending.preview, null, 2)}
              </pre>
              <div
                data-testid="customer-email-attachments-mode"
                className="text-[11px] rounded-md bg-amber-50 border border-amber-200 text-amber-900 p-2"
              >
                Linked references are <strong>audit-only</strong> in this phase — no binary file is attached to the outgoing email. The recipient receives only the HTML body you authorised.
              </div>
              <div className="text-[11px] text-neutral-500">
                Any edit after this will invalidate the token and require a new preview.
              </div>
            </div>
          )}

          <DialogFooter>
            {!pending ? (
              <Button
                data-testid="customer-email-preview-btn"
                onClick={handlePreview}
              >
                Preview & prepare
              </Button>
            ) : (
              <>
                <Button
                  data-testid="customer-email-reset-btn"
                  variant="outline"
                  disabled={sending}
                  onClick={() => setPending(null)}
                >
                  Back
                </Button>
                <Button
                  data-testid="customer-email-confirm-send-btn"
                  disabled={sending}
                  onClick={handleConfirmSend}
                >
                  {sending ? "Sending…" : "Confirm & send"}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
